import type { NavItem } from "@acme/types";
import { cn } from "@acme/ui";
import { Link, useLocation } from "@tanstack/react-router";
import { useEffect, useId, useState } from "react";
import { getCtaConfig } from "~/lib/constants/navbar";
import * as m from "~/paraglide/messages.js";
import { LanguageSwitcher } from "./language-switcher";
import { SiteLogo } from "./site-logo";

/**
 * The design's header: a 90px sticky bar carrying the wordmark, the link row and
 * one call-to-action. It is flat — no glass, no backdrop blur, no scroll-reactive
 * chrome — because the system has exactly one transition and nothing that moves
 * on scroll. Layout and colour live in `styles/lindevo/chrome.css`.
 *
 * The EN/LV switch closes the link row. /chat renders no footer, so the footer
 * could not stay its only home; one control in the chrome beats one per
 * layout. It rides the row into the disclosure panel below 1100px, where a
 * fourth control in the bar would push the call off a 390px screen.
 *
 * Below 1100px the link row becomes a disclosure panel under the bar rather
 * than a drawer: seven links do not need a sheet, and a plain panel keeps the
 * focus order intact without a focus trap. The breakpoint moved up from 900px
 * when the client's export promoted Projects and Blog out of the footer — see
 * `lib/constants/navbar.ts`.
 */
export function Header({ links }: Readonly<{ links: NavItem[] }>) {
  const [isOpen, setIsOpen] = useState(false);
  const navId = useId();
  const pathname = useLocation({ select: (location) => location.pathname });
  const cta = getCtaConfig();

  // Route changes leave the panel open otherwise — the header never unmounts.
  // The effect is keyed on pathname rather than reading it, which is the point.
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on pathname
  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen]);

  return (
    <header className="site-header">
      {/* Without JS the menu button is inert and the panel never opens, so
          below 1100px the links drop into the flow under the bar instead, and
          the header stops sticking so a tall link row does not ride along. The
          chat shell is viewport-locked, so it is released to grow and scroll
          or the lower links would be clipped on short screens. */}
      <noscript>
        <style>{`@media (max-width: 1100px) {
  .site-header { position: static; }
  .chat-page { height: auto; min-height: 100dvh; overflow: visible; }
  .nav { flex-wrap: wrap; height: auto; }
  .nav .logo { align-items: center; height: 90px; }
  .nav .menu { display: none; }
  .nav .header-call { margin-left: auto; }
  .nav nav { position: static; display: flex; order: 1; flex-basis: 100%; padding-inline: 0; }
}
@media (max-width: 720px) {
  .nav .logo { height: 76px; }
}`}</style>
      </noscript>
      <div className="nav wrap">
        <SiteLogo />

        <button
          aria-controls={navId}
          aria-expanded={isOpen}
          className="menu"
          onClick={() => setIsOpen((open) => !open)}
          type="button"
        >
          {m.nav_menu()}
          <span aria-hidden="true">{isOpen ? "✕" : "☰"}</span>
        </button>

        <nav
          aria-label={m.nav_main_label()}
          className={cn(isOpen && "is-open")}
          id={navId}
        >
          {links.map((link) => (
            <Link
              activeOptions={{ exact: link.href === "/" }}
              activeProps={{ "aria-current": "page" }}
              key={link.href}
              to={link.href}
            >
              {link.title}
            </Link>
          ))}
          {/* The route to /chat, shown only where the floating launcher is not.
              Below 720px the bands go full-bleed and a fixed corner control sits
              on top of the page's own buttons, so the launcher hides and this
              row takes over. See `.nav-ask` in chrome.css. */}
          <Link
            activeProps={{ "aria-current": "page" }}
            className="nav-ask"
            to="/chat"
          >
            {m.chatbot_launcher_label()}
          </Link>
          <LanguageSwitcher />
        </nav>

        <a
          className="button button-solid header-call"
          href={cta.href}
          rel="noopener"
          target="_blank"
        >
          {cta.label} <span aria-hidden="true">↗</span>
        </a>
      </div>
    </header>
  );
}
